import { useEffect, useRef } from 'react';
import { inSpeedRun, madeDuringRun, asNewcomerAchievements, saveRunAchievementPrefs, runSignal, runAchievementCounts } from '@/lib/speedRun';
import { RUN_SIGNAL } from '@/lib/speedRunPath.core';
import { track } from '@/lib/analytics';
import { requestReview } from '@/components/engagement/ReviewPrompt';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { engagementApi, speedRunApi } from '@/api/client';
import { getViewAsUserId } from '@/lib/viewAs';
import { celebrate, celebrateWin } from '@/lib/celebrate.core';
import { celebrationSize, periodSince, reviewMomentFor, toCelebrate, weekStartISO, type Milestone, type PerfPeriod } from '@/lib/engagement.core';

/****************
 * The engagement data the Dashboard, Traders and Get Started read (2026-10-21).
 * Viewing another account (?viewAs) reads their data but records nothing:
 * no visit, no celebration marked.
 ****************/

export function useTraderPicks(venue?: string) {
  return useQuery({
    queryKey: ['trader-picks', venue ?? ''],
    queryFn: () => engagementApi.picks(venue),
    staleTime: 10 * 60_000,
    select: (r) => r.picks,
  });
}

/** One visit per page load; the server decides when a new visit starts. */
export function useVisit() {
  return useQuery({
    queryKey: ['me-visit'],
    queryFn: () => (getViewAsUserId() ? Promise.resolve({ since: null, checkins_this_week: 0 }) : engagementApi.visit()),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

export function useRecap() {
  const visit = useVisit();
  return useQuery({
    queryKey: ['me-recap', visit.data?.since ?? 'day'],
    queryFn: () => engagementApi.recap(visit.data?.since),
    // A failed visit still shows the last 24 hours, never a false "quiet".
    enabled: visit.isSuccess || visit.isError,
    staleTime: 60_000,
  });
}

/** The recap from a period's start (the Dashboard's performance card, 10-25); off for "all". */
const ALL_SINCE = '2020-01-01T00:00:00Z';

export function usePeriodRecap(period: PerfPeriod) {
  const visit = useVisit();
  const visitSince = visit.data?.since ?? null;
  return useQuery({
    queryKey: ['me-recap-period', period, period === 'visit' ? visitSince : null],
    // "All" reads every closed trade (up to the recap's cap) so Best and Hardest stay (10-26).
    queryFn: () => engagementApi.recap(periodSince(period, visitSince) ?? ALL_SINCE),
    enabled: period !== 'visit' || visit.isSuccess || visit.isError,
    staleTime: 60_000,
  });
}

export function useMilestones() {
  return useQuery({
    queryKey: ['me-milestones', inSpeedRun()],
    // A speed run: milestones reached before it read as not reached (lib/speedRun.ts).
    queryFn: async () => {
      const s = await engagementApi.milestones();
      if (!inSpeedRun()) return s;
      const milestones = s.milestones.map((m) => (m.done && !madeDuringRun(m.achieved_at) ? { ...m, done: false, celebrated: true } : m));
      return { ...s, milestones, next: milestones.find((m) => !m.done) ?? null };
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

/**
 * Celebrates each milestone reached and not yet celebrated on any device, one
 * after another, then records it so the other device does not repeat it.
 * `show` puts the milestone's own message on screen (a toast).
 */
export function useMilestoneCelebrations(show: (m: Milestone) => void) {
  const { data } = useMilestones();
  const qc = useQueryClient();
  const done = useRef(new Set<string>());
  const mark = useMutation({
    mutationFn: (key: string) => engagementApi.celebrated(key),
    onSettled: () => qc.invalidateQueries({ queryKey: ['me-milestones'] }),
  });
  useEffect(() => {
    if (getViewAsUserId()) return;
    const due = toCelebrate(data).filter((m) => !done.current.has(m.key));
    due.forEach((m, i) => {
      done.current.add(m.key);
      setTimeout(() => {
        celebrate(celebrationSize(m.key));
        show(m);
        mark.mutate(m.key);
        track('milestone_celebrated', m.key);
        // First session or first win: maybe ask for a review (the server decides).
        const moment = reviewMomentFor(m.key);
        if (moment) requestReview(moment);
      }, i * 2800);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
}

/**
 * A winning close while the app is open: confetti, the chime and a toast
 * naming it, with a mute. Checked every minute against the trades closed since
 * this page opened; each trade once. Losses are not celebrated and get no
 * prompt (Guillaume, 2026-10-21: nothing pushes copying more after a loss).
 */
export function useWinCelebrations(show: (t: { symbol: string; pnl: number; session: number; paper: boolean }) => void) {
  const openedAt = useRef(new Date().toISOString());
  const seen = useRef(new Set<string>());
  const { data } = useQuery({
    queryKey: ['me-recap-live', openedAt.current],
    queryFn: () => engagementApi.recap(openedAt.current),
    refetchInterval: 60_000,
    enabled: !getViewAsUserId(),
  });
  useEffect(() => {
    if (!data) return;
    for (const side of [data.live, data.paper]) {
      const best = side.best;
      if (best && best.realized_pnl > 0 && !seen.current.has(best.id)) {
        seen.current.add(best.id);
        celebrateWin();
        show({ symbol: best.symbol, pnl: best.realized_pnl, session: best.subscription_id, paper: best.is_paper });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
}

/** This week's closed trades (Monday, the person's own time), for the weekly card. */
export function useWeekRecap() {
  const since = weekStartISO();
  return useQuery({ queryKey: ['me-recap-week', since], queryFn: () => engagementApi.recap(since), staleTime: 5 * 60_000 });
}

/** Quiz records and the badges (rest/engagement_quiz.go), 2026-10-22. */
export function useQuizzes() {
  // A speed run: no quiz taken yet, as a newcomer (lib/speedRun.ts).
  return useQuery({
    queryKey: ['me-quiz', inSpeedRun()],
    queryFn: async () => (inSpeedRun() ? { quizzes: [] } : engagementApi.quiz()),
    staleTime: 60_000,
  });
}

/** Saves an attempt; viewing another account records nothing. */
export function useQuizResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { quiz: string; score: number; total: number }) =>
      getViewAsUserId() ? Promise.resolve({ perfect: body.score === body.total, first_perfect: false }) : engagementApi.quizResult(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-quiz'] }),
  });
}

/** Achievements, the showcase and the goal (rest/engagement_achievements.go), 2026-10-24. */
export function useAchievements() {
  // A speed run: nothing reached, and only what was followed during the run (lib/speedRun.ts).
  return useQuery({
    queryKey: ['me-achievements', inSpeedRun()],
    queryFn: async () => {
      const s = await engagementApi.achievements();
      if (!inSpeedRun()) return s;
      const [subs, bts] = await Promise.all([
        speedRunApi.get('/api/v1/replicator/subscriptions').catch(() => []),
        speedRunApi.get('/api/v1/backtests?limit=200').catch(() => []),
      ]);
      return asNewcomerAchievements(s, runAchievementCounts(subs, Array.isArray(bts) ? bts : bts?.backtests ?? []));
    },
    staleTime: 60_000,
  });
}

/** Viewing another account changes nothing. */
export function useSaveAchievements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { showcase?: string[]; goal?: string; goals?: string[] }) =>
      getViewAsUserId() ? Promise.reject(new Error('view only'))
        // A speed run keeps its choices in this browser, never on the account (lib/speedRun.ts).
        : inSpeedRun() ? Promise.resolve((saveRunAchievementPrefs(body), (body.goals?.length || body.goal) && runSignal(RUN_SIGNAL.goalsSet), { showcase: body.showcase ?? [], goal: body.goal ?? '' }))
        : engagementApi.saveAchievements(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-achievements'] }),
  });
}
